// Simulations queue view (simulations.md): active strip of live batch cards
// (pending/running, hero placement) above the terminal history table. Rows
// never open a per-batch route — "View in Transcripts" is the primary action
// (`/transcripts?batch=`; docs/09 §4). Rerun = frozen snapshot replay;
// Duplicate prefills `/simulations/new?from=`. No Cancel: nothing in the API
// writes `canceled` today (worker-side cancel is a named follow-up), and no
// Delete from the queue — a launched batch is an immutable execution record.
// Props-pure; the container owns queries and mutations.
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { docUrl } from "../../lib/docLinks";
import { isShowcaseMode } from "../../lib/showcaseMode";
import { formatDateTime } from "../../lib/format";
import {
  Badge,
  Button,
  EmptyState,
  HelpTip,
  Inline,
  Section,
  Stack,
  Text,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { ActiveSimulationCard } from "./ActiveSimulationCard";
import {
  batchDisplayLabel,
  historyStatusTone,
  scheduledTemplateLabel,
} from "./batchPresentation";

type BatchResponse = components["schemas"]["BatchResponse"];

interface HistoryColumnInput {
  isRerunPending: boolean;
  onDuplicate: (batchId: string) => void;
  onOpenInTranscripts: (batchId: string) => void;
  onRerun: (batchId: string) => void;
  templateNamesById: ReadonlyMap<string, string>;
}

function historyColumns({
  isRerunPending,
  onDuplicate,
  onOpenInTranscripts,
  onRerun,
  templateNamesById,
}: HistoryColumnInput): Array<ColumnDef<BatchResponse>> {
  return [
    {
      header: "Batch",
      id: "batch",
      accessorFn: (batch) => batchDisplayLabel(batch),
      cell: ({ row }) => (
        <Stack gap={1}>
          <Text>{batchDisplayLabel(row.original)}</Text>
          {scheduledTemplateLabel(row.original, templateNamesById) ===
          null ? null : (
            <Badge tone="neutral">
              {scheduledTemplateLabel(row.original, templateNamesById)}
            </Badge>
          )}
          {row.original.parent_batch_id !== null ? (
            <Text variant="muted">Rerun of an earlier batch</Text>
          ) : null}
        </Stack>
      ),
    },
    {
      header: "Status",
      id: "status",
      accessorFn: (batch) => batch.status,
      cell: ({ row }) => (
        <Badge tone={historyStatusTone(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: "Launched",
      accessorKey: "created_at",
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      header: "Personas",
      id: "personas",
      accessorFn: (batch) => batch.persona_ids?.length ?? 0,
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <Inline gap={2}>
          <Button
            size="sm"
            type="button"
            variant="secondary"
            onClick={() => {
              onOpenInTranscripts(row.original.id);
            }}
          >
            View in Transcripts
          </Button>
          {isShowcaseMode ? null : (
            <>
              <Button
                disabled={isRerunPending}
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => {
                  onRerun(row.original.id);
                }}
              >
                Rerun
              </Button>
              <Button
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => {
                  onDuplicate(row.original.id);
                }}
              >
                Duplicate
              </Button>
            </>
          )}
        </Inline>
      ),
    },
  ];
}

export interface SimulationQueueProps {
  activeBatches: BatchResponse[];
  errorMessage?: string;
  hasNextPage: boolean;
  highlightedBatchId?: string;
  historyBatches: BatchResponse[];
  isFetchingNextPage: boolean;
  isPending: boolean;
  isRerunPending: boolean;
  listStateKey: string;
  onDuplicate: (batchId: string) => void;
  onLaunchFirst: () => void;
  onLoadMore: () => void;
  onOpenInTranscripts: (batchId: string) => void;
  onRerun: (batchId: string) => void;
  templateNamesById: ReadonlyMap<string, string>;
}

export function SimulationQueue({
  activeBatches,
  errorMessage,
  hasNextPage,
  highlightedBatchId,
  historyBatches,
  isFetchingNextPage,
  isPending,
  isRerunPending,
  listStateKey,
  onDuplicate,
  onLaunchFirst,
  onLoadMore,
  onOpenInTranscripts,
  onRerun,
  templateNamesById,
}: SimulationQueueProps) {
  return (
    <Stack gap={6}>
      {activeBatches.length > 0 ? (
        <Section heading="Active">
          <Stack gap={3}>
            {activeBatches.map((batch) => (
              <ActiveSimulationCard
                batch={batch}
                isHighlighted={batch.id === highlightedBatchId}
                key={batch.id}
                onOpenInTranscripts={onOpenInTranscripts}
                templateNamesById={templateNamesById}
              />
            ))}
          </Stack>
        </Section>
      ) : null}
      {/* "History" earns its place only as the contrast to "Active". With no
          run in flight it is a lone heading restating the RUNS tab above it,
          and it spends ~40px of the above-the-fold budget saying nothing. */}
      <Section heading={activeBatches.length > 0 ? "History" : undefined}>
        <PaginatedTableSection
          ariaLabel="Simulation history"
          columns={historyColumns({
            isRerunPending,
            onDuplicate,
            onOpenInTranscripts,
            onRerun,
            templateNamesById,
          })}
          emptyState={
            <EmptyState
              action={
                <Button onClick={onLaunchFirst} type="button">
                  Create simulation
                </Button>
              }
              help={
                <HelpTip
                  docHref={docUrl("concepts/simulated-testing")}
                  docLabel="Simulated testing"
                  label="About simulations"
                >
                  {
                    "A Batch runs simulated conversations against your Agent over a Connection, then scores each Transcript against the Agent's Markers."
                  }
                </HelpTip>
              }
              title="No simulations"
            />
          }
          errorMessage={errorMessage}
          getRowId={(batch) => batch.id}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isPending={isPending}
          items={historyBatches}
          listStateKey={listStateKey}
          onLoadMore={onLoadMore}
          skeletonLabel="Loading simulations"
        />
      </Section>
    </Stack>
  );
}
